import { useState } from 'react'
import {
  Terminal,
  ExternalLink,
  MoreVertical,
  Play,
  Square,
  RefreshCw,
  Share2,
  Copy,
  Trash2,
  Loader2,
} from 'lucide-react'
import { type App } from '../lib/api'
import { withMountBase } from '../lib/mountBase'
import { ConfirmDialog } from './ui/ConfirmDialog'

type Permission = 'owner' | 'admin' | 'edit' | 'view'

interface AppActionsProps {
  app: App
  permission?: Permission
  onStart?: () => Promise<void>
  onStop?: () => Promise<void>
  onRebuild?: () => Promise<void>
  onShare?: () => void
  onDelete?: () => Promise<void>
  size?: 'sm' | 'md' | 'lg'
  showLabels?: boolean
}

export default function AppActions({
  app,
  permission = 'owner',
  onStart,
  onStop,
  onRebuild,
  onShare,
  onDelete,
  size = 'md',
  showLabels = false,
}: AppActionsProps) {
  const [showMenu, setShowMenu] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const canEdit = permission === 'owner' || permission === 'admin' || permission === 'edit'
  const canDelete = permission === 'owner'

  // In the new per-app workspace container, the app code is at /home/coder/workspace/main
  const appFolder = `/home/coder/workspace/main`
  const workspacePath = withMountBase('/workspace', window.location.pathname)
  const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
  const appUrl = `${window.location.origin}${appPath}`

  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
  }

  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  async function handleAction(action: () => Promise<void>, name: string) {
    setActionLoading(name)
    try {
      await action()
    } finally {
      setActionLoading(null)
      setShowMenu(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      {/* Workspace - Primary action, emphasized (edit+ only) */}
      {canEdit && (
        <a
          href={`${workspacePath}?app=${encodeURIComponent(app.name)}&folder=${encodeURIComponent(appFolder)}&url=${encodeURIComponent(appUrl)}`}
          className={`${sizeClasses[size]} rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center gap-2`}
          title="Open Workspace"
          onClick={(e) => e.stopPropagation()}
        >
          <Terminal className={iconSizes[size]} />
          {showLabels && <span className="text-sm font-medium">Workspace</span>}
        </a>
      )}

      {/* Open in new window */}
      <a
        href={app.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${sizeClasses[size]} rounded-lg bg-secondary hover:bg-secondary/80 transition-colors`}
        title="Open App"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className={iconSizes[size]} />
      </a>

      {/* More menu */}
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowMenu(!showMenu)
          }}
          className={`${sizeClasses[size]} rounded-lg hover:bg-secondary transition-colors`}
          aria-label="More actions"
          title="More actions"
        >
          <MoreVertical className={iconSizes[size]} />
        </button>

        {showMenu && (
          <>
            <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowMenu(false) }} />
            <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg z-20 py-1">
              {/* Start/Stop (edit+ only) */}
              {canEdit && (
                app.status === 'stopped' || app.status === 'error' ? (
                  onStart && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAction(onStart, 'start')
                      }}
                      disabled={actionLoading !== null}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 text-green-500 disabled:opacity-50"
                    >
                      {actionLoading === 'start' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                      Start App
                    </button>
                  )
                ) : (
                  onStop && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleAction(onStop, 'stop')
                      }}
                      disabled={actionLoading !== null}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 disabled:opacity-50"
                    >
                      {actionLoading === 'stop' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      Stop App
                    </button>
                  )
                )
              )}

              {/* Rebuild (edit+ only) */}
              {canEdit && onRebuild && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAction(onRebuild, 'rebuild')
                  }}
                  disabled={actionLoading !== null}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 text-orange-500 disabled:opacity-50"
                  title="Rebuild container (reinstalls dependencies)"
                >
                  {actionLoading === 'rebuild' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Rebuild
                </button>
              )}

              {canEdit && <hr className="my-1 border-border" />}

              {/* Share (edit+ only) */}
              {canEdit && onShare && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMenu(false)
                    onShare()
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              )}

              {/* Copy URL (everyone) */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  navigator.clipboard.writeText(app.url)
                  setCopied(true)
                  setTimeout(() => { setCopied(false); setShowMenu(false) }, 1200)
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Copied!' : 'Copy URL'}
              </button>

              {/* Delete (owner only) */}
              {canDelete && onDelete && (
                <>
                  <hr className="my-1 border-border" />
                  <ConfirmDialog
                    trigger={
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    }
                    title="Delete App"
                    description={`Delete "${app.display_name || app.name}"? You can restore it for 30 days.`}
                    confirmLabel="Delete"
                    variant="destructive"
                    onConfirm={onDelete}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
